import { productTypes } from '@src/cart/cartProductTypes';
import shopifyHttpGateway from '@src/services/shopifyHttpGateway';
import subjectsRepository from '@src/subjects/subjectsRepository';
import teachersRepository from '@src/teachers/teachersRepository';
import { action, makeObservable, observable, reaction } from 'mobx';
import { getLessonByTeacherIdQuery } from '../checkoutQueries';

class CheckoutLessonRepository {
  lessonPm = {
    teacherId: null,
    calendar: null,
    day: null,
    time: null,
    productVariants: null,
    lessonStudentCount: null,
    lessonMax: null,
    lessonType: null,
  };

  constructor() {
    makeObservable(this, {
      lessonPm: observable,
      updateTeacherData: action,
      selectLessonTime: action,
    });
  }

  load(callback) {
    reaction(
      () => [
        this.lessonPm,
        this.lessonPm.day,
        this.lessonPm.time,
        this.lessonPm.lessonType,
        this.lessonPm.lessonStudentCount,
        this.lessonPm.lessonMax,
        this.lessonPm.teacherName,
        this.lessonPm.calendar,
      ],
      () => callback(this.lessonPm),
      { fireImmediately: true }
    );
  }

  async selectTeacher(teacher) {
    const teacherPm = await teachersRepository.getTeacherDetailsInfoById(
      teacher.slug
    );

    const productsDto = await this.loadProductFromShopify(teacher.id);
    const subjectsPm = subjectsRepository.getSubjects();
    const lessonPm = {
      teacherId: teacher.id,
      teacherAvatarUrl: teacherPm.avatarUrl,
      teacherName: teacherPm.name,
      subjects: (teacherPm.subjects || []).map((subject) => {
        let mergeSubject =
          subjectsPm.find((item) => item.uniqueId === subject.subjectId) || {};
        mergeSubject.qualification = subject.qualification;
        mergeSubject.taughtForClasses = subject.taughtForClasses;
        mergeSubject.taughtForStudents = subject.taughtForStudents;
        mergeSubject.taughtForAdults = subject.taughtForAdults;
        return subject;
      }),
      calendar: teacherPm.calendar,
      lessonMax: teacherPm.groupLessonMax,
      productVariants: productsDto.variants,
      day: null,
      time: null,
      price: teacherPm.price,
      currency: teacherPm.currency,
    };

    this.updateTeacherData(lessonPm);
  }
  selectLessonTime({ day, time, lessonType, lessonStudentCount, lessonMax }) {
    this.lessonPm.day = day;
    this.lessonPm.time = time;
    this.lessonPm.lessonType = lessonType || 1;
    this.lessonPm.lessonStudentCount = lessonStudentCount;

    if (lessonMax) {
      this.lessonPm.lessonMax = lessonMax;
    }
  }

  getSelectedTime() {
    if (!this.lessonPm.day) {
      return null;
    }
    return {
      day: this.lessonPm.day,
      time: this.lessonPm.time,
    };
  }

  async loadProductFromShopify(id) {
    const query = getLessonByTeacherIdQuery(id);
    const responseDto = await shopifyHttpGateway.post({ query });

    if (
      !responseDto?.data?.products?.edges?.length ||
      responseDto.data.products.edges.length === 0
    ) {
      return {};
    }

    const lesson = responseDto.data.products.edges.find((edge) => {
      return (
        edge.node.productType === productTypes.lesson.shopifyProductType ||
        edge.node.productType === productTypes.lesson.shopifyProductTypeRo
      );
    });

    if (!lesson) {
      return {};
    }

    const lessonProductPm = {
      id: lesson.node.id,
      variants: lesson.node.variants.edges.map((edge) => {
        return {
          id: edge.node.id,
          price: edge.node.price.amount,
          title: edge.node.title,
          sku: edge.node.sku,
          currencyCode: lesson.node.priceRange.minVariantPrice.currencyCode,
        };
      }),
    };

    return lessonProductPm;
  }

  updateTeacherData(lessonPm) {
    this.lessonPm.teacherId = lessonPm.teacherId;
    this.lessonPm.teacherAvatarUrl = lessonPm.teacherAvatarUrl;
    this.lessonPm.teacherName = lessonPm.teacherName;
    this.lessonPm.subjects = lessonPm.subjects;
    this.lessonPm.calendar = lessonPm.calendar;
    this.lessonPm.productVariants = lessonPm.productVariants;
    this.lessonPm.day = lessonPm.day;
    this.lessonPm.time = lessonPm.time;
    this.lessonPm.lessonMax = lessonPm.lessonMax;
    this.lessonPm.price = lessonPm.price;
    this.lessonPm.currency = lessonPm.currency;
  }
}

const checkoutLessonRepository = new CheckoutLessonRepository();

export default checkoutLessonRepository;
